// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {DateInput, Icon, Space, Styled, Popover} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {PopoverType, usePopover, useQuery, useResponsive, useState} from '@supermove/hooks';
import {JobEventModel, ProjectModel} from '@supermove/models';
import {Datetime} from '@supermove/utils';

// App
import Button from '@shared/design/components/Button';
import Checkbox from '@shared/design/components/Checkbox';
import FieldInput from '@shared/design/components/Field/FieldInput';
import MultiDropdownCheckboxInput from '@shared/design/components/Field/MultiDropdownCheckboxInput';
import Text from '@shared/design/components/Text';
import PopoverFilterWithFooter from '@shared/modules/App/components/PopoverFilterWithFooter';
import {
  FiltersType,
  SetFiltersType,
} from '@shared/modules/Project/components/widgets/ProjectEventLogWidget';

const ContentContainer = Styled.View`
`;

const FiltersRow = Styled.View<{index: number}>`
  z-index: ${({index}) => 100 - index};
  flex-direction: row;
  align-items: flex-end;
`;

const FilterContainer = Styled.View<{index: number}>`
  z-index: ${({index}) => 100 - index};
`;

const getIsPresent = (value: FiltersType[keyof FiltersType]) => {
  if (_.isArray(value)) {
    return !_.isEmpty(value);
  }
  return !!value;
};

const getFilterCount = ({filters}: {filters: FiltersType}) => {
  const filterCountByIsPresent = _.countBy(Object.values(filters), (value) => getIsPresent(value));
  return _.get(filterCountByIsPresent, 'true', 0);
};

const getMinMaxDates = ({project}: {project: ProjectModel}) => {
  if (!project) {
    return {minDate: null, maxDate: null};
  }
  const dates = project.jobEvents.map((event: JobEventModel) => event.date);
  const minDate = _.minBy(dates, (date) => date);
  const maxDate = _.maxBy(dates, (date) => date);
  return {minDate: Datetime.fromDate(minDate), maxDate: Datetime.fromDate(maxDate)};
};

const getCreatorOptions = ({project}: {project: ProjectModel}) => {
  const creators = project.jobEvents.reduce(
    (result, {creator}) =>
      creator
        ? {...result, [creator.id]: {value: creator.id, label: creator.fullName}}
        : {...result, supermove: {value: 0, label: 'Supermove'}},
    {},
  );
  return _.orderBy(Object.values(creators), ['label']);
};

const DateFilter = ({
  index,
  dateKey,
  label,
  internalFilters,
  setInternalFilters,
  project,
}: {
  index: number;
  dateKey: 'fromDate' | 'toDate';
  label?: string;
  internalFilters: FiltersType;
  setInternalFilters: SetFiltersType;
  project: ProjectModel;
}) => {
  const {minDate, maxDate} = getMinMaxDates({project});
  const responsive = useResponsive();

  return (
    <FilterContainer index={index} style={{flex: 1}}>
      <FieldInput.LabelText isResponsive>{label}</FieldInput.LabelText>
      <Space height={4} />
      <DateInput
        placeholder={'MM/DD/YYYY'}
        setFieldValue={() => {}}
        onChangeDate={(date) => {
          setInternalFilters({...internalFilters, [dateKey]: date});
        }}
        value={internalFilters[dateKey]}
        style={{width: '100%', ...(responsive.desktop ? {} : {height: 48, fontSize: 16})}}
        position={'bottom'}
        minDate={minDate}
        maxDate={maxDate}
      />
    </FilterContainer>
  );
};

const DateFilters = ({
  index,
  project,
  internalFilters,
  setInternalFilters,
}: {
  index: number;
  project: ProjectModel;
  internalFilters: FiltersType;
  setInternalFilters: SetFiltersType;
}) => {
  return (
    <FiltersRow index={index}>
      <DateFilter
        index={0}
        dateKey={'fromDate'}
        label={'Date Range'}
        internalFilters={internalFilters}
        setInternalFilters={setInternalFilters}
        project={project}
      />
      <Space width={8} />
      <Text.Body style={{paddingBottom: 12}}>-</Text.Body>
      <Space width={8} />
      <DateFilter
        index={1}
        dateKey={'toDate'}
        internalFilters={internalFilters}
        setInternalFilters={setInternalFilters}
        project={project}
      />
    </FiltersRow>
  );
};

const MobileDateFilters = ({
  index,
  project,
  internalFilters,
  setInternalFilters,
}: {
  index: number;
  project: ProjectModel;
  internalFilters: FiltersType;
  setInternalFilters: SetFiltersType;
}) => {
  return (
    <FilterContainer index={index}>
      <DateFilter
        index={0}
        dateKey={'fromDate'}
        label={'From'}
        internalFilters={internalFilters}
        setInternalFilters={setInternalFilters}
        project={project}
      />
      <Space height={16} />
      <DateFilter
        index={1}
        dateKey={'toDate'}
        label={'To'}
        internalFilters={internalFilters}
        setInternalFilters={setInternalFilters}
        project={project}
      />
    </FilterContainer>
  );
};

const CreatorsFilter = ({
  index,
  project,
  internalFilters,
  setInternalFilters,
}: {
  index: number;
  project: ProjectModel;
  internalFilters: FiltersType;
  setInternalFilters: SetFiltersType;
}) => {
  return (
    <FilterContainer index={index}>
      <FieldInput.LabelText isResponsive>Creator(s)</FieldInput.LabelText>
      <Space height={4} />
      <MultiDropdownCheckboxInput
        options={project ? getCreatorOptions({project}) : []}
        isLoading={!project}
        placeholder={'Select creator(s)'}
        style={{width: '100%'}}
        value={internalFilters.creatorIds || []}
        onChangeValue={(creatorIds) => {
          // @ts-expect-error Need to use generics to pass option values to onChangeValue.
          setInternalFilters({...internalFilters, creatorIds});
        }}
        isSearchable
        isResponsive
        usePills
      />
    </FilterContainer>
  );
};

const JobsFilter = ({
  index,
  project,
  internalFilters,
  setInternalFilters,
}: {
  index: number;
  project: ProjectModel;
  internalFilters: FiltersType;
  setInternalFilters: SetFiltersType;
}) => {
  return (
    <FilterContainer index={index}>
      <FieldInput.LabelText isResponsive>Job(s)</FieldInput.LabelText>
      <Space height={4} />
      <MultiDropdownCheckboxInput
        options={
          project
            ? project.activeJobs.map((job) => ({
                value: job.id,
                label: job.fullName,
              }))
            : []
        }
        isLoading={!project}
        placeholder={'Select job(s)'}
        style={{width: '100%'}}
        value={internalFilters.jobIds || []}
        onChangeValue={(jobIds) => {
          // @ts-expect-error Need to use generics to pass option values to onChangeValue.
          setInternalFilters({...internalFilters, jobIds});
        }}
        isSearchable
        isResponsive
        usePills
      />
    </FilterContainer>
  );
};

const IsJobsOnly = ({
  index,
  internalFilters,
  setInternalFilters,
}: {
  index: number;
  internalFilters: FiltersType;
  setInternalFilters: SetFiltersType;
}) => {
  const responsive = useResponsive();
  const isJobsOnly = _.get(internalFilters, 'isJobsOnly');
  return (
    <FilterContainer index={index}>
      <Checkbox
        isChecked={isJobsOnly}
        handleToggle={() => setInternalFilters({...internalFilters, isJobsOnly: !isJobsOnly})}
        childrenRight
        style={{alignItems: 'center'}}
      >
        <Space width={12} />
        <FieldInput.LabelText isResponsive style={responsive.desktop ? null : {marginTop: -2}}>
          Exclude project-level events
        </FieldInput.LabelText>
      </Checkbox>
    </FilterContainer>
  );
};

const ProjectEventFiltersPopoverContent = ({
  projectUuid,
  internalFilters,
  setInternalFilters,
}: {
  projectUuid: string;
  internalFilters: FiltersType;
  setInternalFilters: SetFiltersType;
}) => {
  const {data} = useQuery(ProjectEventLogFilters.query, {
    fetchPolicy: 'cache-and-network',
    variables: {projectUuid},
  });

  const project = data?.project;
  const responsive = useResponsive();

  return (
    <ContentContainer>
      {responsive.desktop ? (
        <DateFilters
          index={0}
          project={project}
          internalFilters={internalFilters}
          setInternalFilters={setInternalFilters}
        />
      ) : (
        <MobileDateFilters
          index={0}
          project={project}
          internalFilters={internalFilters}
          setInternalFilters={setInternalFilters}
        />
      )}
      <Space height={16} />
      <CreatorsFilter
        index={1}
        project={project}
        internalFilters={internalFilters}
        setInternalFilters={setInternalFilters}
      />
      <Space height={16} />
      <JobsFilter
        index={2}
        project={project}
        internalFilters={internalFilters}
        setInternalFilters={setInternalFilters}
      />
      <Space height={16} />
      <IsJobsOnly
        index={3}
        internalFilters={internalFilters}
        setInternalFilters={setInternalFilters}
      />
      <Space height={16} />
    </ContentContainer>
  );
};

const ProjectEventFiltersPopover = ({
  popover,
  projectUuid,
  filters,
  setFilters,
}: {
  popover: PopoverType;
  projectUuid: string;
  filters: FiltersType;
  setFilters: SetFiltersType;
}) => {
  const [internalFilters, setInternalFilters] = useState(filters);

  return (
    <PopoverFilterWithFooter
      activeFiltersCount={getFilterCount({filters: internalFilters})}
      popover={popover}
      handleClear={() =>
        setInternalFilters({
          fromDate: '',
          toDate: '',
          creatorIds: [],
          jobIds: [],
          isJobsOnly: false,
        })
      }
      handleApply={() => {
        setFilters(internalFilters);
        popover.handleClose();
      }}
    >
      <ProjectEventFiltersPopoverContent
        projectUuid={projectUuid}
        internalFilters={internalFilters}
        setInternalFilters={setInternalFilters}
      />
    </PopoverFilterWithFooter>
  );
};

const ProjectEventLogFilters = ({
  projectUuid,
  filters,
  setFilters,
}: {
  projectUuid: string;
  filters: FiltersType;
  setFilters: SetFiltersType;
}) => {
  const eventLogFiltersPopover = usePopover({
    name: 'Project Event Log Filters Popover',
  });
  const filterCount = getFilterCount({filters});

  return (
    <React.Fragment>
      <Popover.Content innerRef={eventLogFiltersPopover.ref}>
        <Button
          text={filterCount ? `(${filterCount})` : undefined}
          onPress={eventLogFiltersPopover.handleToggle}
          iconLeft={Icon.Filter}
          isResponsive
        />
      </Popover.Content>
      <ProjectEventFiltersPopover
        key={eventLogFiltersPopover.key}
        popover={eventLogFiltersPopover}
        projectUuid={projectUuid}
        filters={filters}
        setFilters={setFilters}
      />
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectEventLogFilters.query = gql`
  query ProjectEventLogFilters($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      jobEvents {
        id
        date
        creator {
          id
          fullName
        }
      }
      activeJobs {
        id
        fullName
      }
    }
  }
`;

export default ProjectEventLogFilters;
